import { warn } from "@/lib/log";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LOGO_URL } from "@/lib/langs";
import { useAuth } from "@/context/AuthContext";
import { syncPush } from "@/lib/push";

const SupportCtx = createContext({ unread: 0, markRead: () => {}, open: false, setOpen: () => {} });
const BASE_TITLE = document.title;

const notify = (count) => {
  if (!("Notification" in window) || Notification.permission !== "granted" || localStorage.getItem("aurora_push") === "on") return;
  try {
    new Notification("SYSTEM / SpeechFlow Support", { body: `You have ${count} unread message${count > 1 ? "s" : ""}`, icon: LOGO_URL, tag: "aurora-support" });
  } catch (e) {
    warn("System notification failed", e);
  }
};

export function SupportProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const prev = useRef(0);
  const enabled = !!user && user.role !== "admin";

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/support/unread");
      if (data.unread > prev.current) {
        toast.message("New message from SYSTEM / SpeechFlow Support", { description: "Tap the chat icon to read it." });
        notify(data.unread);
      }
      prev.current = data.unread;
      setUnread(data.unread);
    } catch (e) {
      warn("Support poll failed", e);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    syncPush();
    poll();
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
  }, [enabled, poll]);

  useEffect(() => {
    document.title = unread ? `(${unread}) ${BASE_TITLE}` : BASE_TITLE;
  }, [unread]);

  const markRead = useCallback(async () => {
    if (!prev.current) return;
    prev.current = 0;
    setUnread(0);
    try { await api.post("/support/read"); } catch (e) { warn("Mark read failed", e); }
  }, []);

  const value = useMemo(() => ({ unread: enabled ? unread : 0, markRead, open, setOpen, enabled }), [enabled, unread, markRead, open]);
  return <SupportCtx.Provider value={value}>{children}</SupportCtx.Provider>;
}

export const useSupport = () => useContext(SupportCtx);
